/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import Ember from 'ember';
import Editor, { ActionComposerEditorArgs } from 'cio/components/action-composer/base-glimmer/editor';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { task, timeout } from 'ember-concurrency';
import { Framecast } from 'framecast';
import TemplateModel, { TemplateType } from 'cio/models/template';
import LayoutModel from 'cio/models/layout';
import EnvironmentService from 'cio/services/environment';
import AlertsService from 'cio/services/alerts';
import RouterService from '@ember/routing/router-service';
import { EMAIL_COMPOSER_ROLLBACK } from 'cio/services/event-bus';
import AnalyticsService from 'cio/services/analytics';
import AccountService from 'cio/services/account';
import { EmailPreview } from 'cio/utils/previews';
import { EventsType } from 'cio/utils/analytics/events';
import { Header } from 'cio/utils/models/header';
import FeaturesService from 'ember-feature-flags/services/features';
import { ParcelState, PARCEL_EMAIL_EDITOR_SRC, LOAD_TIMEOUT_MS } from 'cio/utils/composer/parcel';

interface ActionComposerTypesParcelEditorComponentArgs extends ActionComposerEditorArgs {
  previewVariables: Record<string, unknown>;
  fetchPreview: () => Promise<void>;
  preview: EmailPreview;
}

interface ParcelEditorSignature {
  Args: ActionComposerTypesParcelEditorComponentArgs;
  Element: HTMLDivElement;
}

type ParcelTemplate = {
  id: string;
  templateType: TemplateType;
  layoutId: string;
  body: string;
  bodyAmp?: string;
  language: string;
  subject: string;
  preheaderText: string;
  recipient: string;
  fromIdentityId: string;
  replyToIdentityId: string;
  bcc: string;
  fakeBcc: string;
  headers: Header[];
  preprocessor: string;
  isTransactionalMessage: boolean;
};

export default class ParcelEditor extends Editor<ParcelEditorSignature> {
  @service environment!: EnvironmentService;
  @service alerts!: AlertsService;
  @service router!: RouterService;
  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @service features!: FeaturesService;

  ParcelState = ParcelState;
  PARCEL_SRC = PARCEL_EMAIL_EDITOR_SRC;

  @tracked state: ParcelState = ParcelState.Init;

  get isLoading() {
    return this.state === ParcelState.Init;
  }

  get isError() {
    return this.state === ParcelState.Error;
  }

  get isAmpEnabled() {
    return this.features.isEnabled('parcel_amp') && this.auth.user?.userFlags.show_parcel_amp;
  }

  @tracked layouts: LayoutModel[] = [];
  @tracked showAssetLibraryModal = false;

  framecast: Framecast | null = null;

  willDestroy() {
    super.willDestroy();

    this.teardownEditorEventListeners();
  }

  fetchLayouts = task({ drop: true }, async () => {
    let activeLayouts = await this.store.query('layout', { archived: false });
    let layouts = activeLayouts.toArray();

    let layout = await this.template?.belongsTo('layout').load();

    // A user can delete a layout from another route and come back to this route. In that case,
    // we need to ensure that the layout isn't destroyed before pushing it to the array.
    if (!isNone(layout) && !layout.isDestroyed) {
      layouts.addObject(layout);
    }

    this.layouts = layouts;
  });

  @action
  setVariables() {
    if (this.framecast == null) {
      return;
    }

    this.framecast.call('setVariables', this.args.previewVariables);
    this.framecast.call('setSentAttributes', this.environment.sentAttributes);
  }

  @action
  setTemplate() {
    if (this.framecast == null) {
      return;
    }

    if (this.template.preprocessor === '') {
      // set to default preprocessor if it's an empty string
      this.template.set('preprocessor', 'premailer');
    }

    let { template } = this;

    if (template == null) {
      throw new Error('Template is missing');
    }

    let node = {
      id: template.id,
      templateType: template.templateType,
      layoutId: template.belongsTo('layout').id(),
      body: template.body,
      language: template.language,
      subject: template.subject,
      preheaderText: template.preheaderText,
      recipient: template.recipient,
      fromIdentityId: template.fromIdentityId,
      replyToIdentityId: template.replyToIdentityId,
      bcc: template.bcc,
      fakeBcc: template.fakeBcc,
      headers: [...template.headers],
      preprocessor: template.preprocessor,
      isTransactionalMessage: this.args.editableEntity.isTransactionalMessage,
    } as ParcelTemplate;

    if (this.isAmpEnabled) {
      node.bodyAmp = template.bodyAmp;
    }

    this.framecast.call('setNode', node);
  }

  attachFramecast = task({ drop: true }, async (iframeElement: HTMLIFrameElement) => {
    if (Ember.testing) {
      // Temp. Avoid error "Cannot read properties of undefined (reading 'getModel')" in tests
      return;
    }

    if (this.state !== ParcelState.Init) {
      return;
    }

    let layoutTask = this.fetchLayouts;
    if (layoutTask.isRunning) {
      await layoutTask.last;
    }

    this.framecast = new Framecast(iframeElement.contentWindow as Window);
    this.setEditorEventListeners();
    this.loadTimeout.perform();
  });

  loadTimeout = task({ restartable: true }, async () => {
    await timeout(LOAD_TIMEOUT_MS);

    this.setEditorErrorState();
  });

  @action
  setEditorErrorState() {
    this.state = ParcelState.Error;
  }

  @action
  setEditorReadyState() {
    this.state = ParcelState.Ready;

    this.setTemplate();
    this.setVariables();

    this.loadTimeout.cancelAll();

    if (this.account.current.currentUser.userFlags.show_parcel_onboarding_tour) {
      this.framecast?.call('showOnboardingTour');
      this.account.current.currentUser.updateUserFlag('show_parcel_onboarding_tour', false);
    }
  }

  @action
  private framecastGetLayouts() {
    return this.layouts.map((l) => ({
      id: l.id,
      name: l.name,
      body: l.body,
      isDefault: l.isDefault,
    }));
  }

  @action
  private framecastGetIdentities() {
    return this.identities.map((i) => ({
      id: i.id,
      name: i.name,
      email: i.email,
      verified: i.verified,
    }));
  }

  @action
  private framecastRedirect(
    routeName: string,
    routeModel: string,
    routeOptions: { queryParams: Record<string, string> },
  ) {
    let transitionParams = [routeModel, routeOptions].filter((item) => item != null);

    // @ts-ignore transitionTo typing doesn't support destructured argument
    this.router.transitionTo(routeName, ...transitionParams);
  }

  @action
  private async framecastGetPreview() {
    await this.args.fetchPreview();

    let { preview } = this.args;

    return {
      subject: preview.subject,
      from: preview.from,
      body: preview.body,
      bodyAmp: preview.body_amp,
      to: preview.to,
      replyTo: preview.reply_to,
      bcc: preview.bcc,
      headers: preview.headers,
      urlParams: preview.url_params,
      links: preview.links,
      errors: {
        subject: preview.errors.subject,
        from: preview.errors.from,
        body: preview.errors.body,
        to: preview.errors.to,
        replyTo: preview.errors.reply_to,
        bcc: preview.errors.bcc,
        headers: preview.errors.headers,
        urlParams: preview.errors.url_params,
      },
    };
  }

  @action
  private async framecastBroadcast({ event, data }: any) {
    if (this.framecast == null) {
      return;
    }

    switch (event) {
      case 'ready':
        this.setEditorReadyState();
        break;
      case 'interactive':
        this.state = ParcelState.Interactive;
        break;
      case 'error':
        this.alerts.error('parcel.notification', data?.message || 'Something went wrong!');
        this.setEditorErrorState();
        break;
      case 'save': {
        let node = (await this.framecast.call('getNode')) as ParcelTemplate | null;

        if (node == null) {
          return;
        }

        let updatedProperties: any = {
          subject: node.subject,
          body: node.body,
          preheaderText: node.preheaderText ?? '',
          recipient: node.recipient,
          bcc: node.bcc,
          fakeBcc: node.fakeBcc,
          headers: node.headers,
          preprocessor: node.preprocessor,
          layout: this.layouts.find((item) => item.id === node?.layoutId),
          fromIdentity: this.identities.find((item) => item.id === node?.fromIdentityId),
          replyToIdentity: this.identities.find((item) => item.id === node?.replyToIdentityId),
        };

        if (this.isAmpEnabled && node.bodyAmp != null) {
          updatedProperties.bodyAmp = node.bodyAmp;
        }

        this.template.setProperties(updatedProperties);
        break;
      }
      case 'notification':
        switch (data?.type) {
          case 'success':
            this.alerts.success('parcel.notification', data.message);
            break;
          case 'warning':
            this.alerts.warning('parcel.notification', data.message);
            break;
          case 'error':
            this.alerts.error('parcel.notification', data.message);
            break;
        }
        break;
      case 'redirect':
        this.router.transitionTo(data.routeName, data.routeModel, data.routeOptions);
        break;
    }
  }

  @action
  private framecastOpenAssetLibrary() {
    this.showAssetLibraryModal = true;
  }

  private setEditorEventListeners() {
    if (this.framecast == null) {
      return;
    }

    this.framecast.on('function:getLayouts', this.framecastGetLayouts);
    this.framecast.on('function:redirect', this.framecastRedirect);
    this.framecast.on('function:getIdentities', this.framecastGetIdentities);
    this.framecast.on('function:getPreview', this.framecastGetPreview);
    this.framecast.on('function:openAssetLibrary', this.framecastOpenAssetLibrary);
    this.framecast.on('function:track', this.framecastTrack);
    this.framecast.on('broadcast', this.framecastBroadcast);
  }

  @action
  framecastTrack(eventName: keyof EventsType['message'], eventData: Record<string, any>) {
    this.analytics.track('message', eventName, {
      ...eventData,
      ...this.args.editableEntity.analyticsData,
      builderType: 'Code',
    });
  }

  private teardownEditorEventListeners() {
    if (this.framecast == null) {
      return;
    }

    this.framecast.off('function:getLayouts', this.framecastGetLayouts);
    this.framecast.off('function:redirect', this.framecastRedirect);
    this.framecast.off('function:getIdentities', this.framecastGetIdentities);
    this.framecast.off('function:getPreview', this.framecastGetPreview);
    this.framecast.off('function:track', this.framecastTrack);
    this.framecast.off('broadcast', this.framecastBroadcast);
    this.framecast = null;
  }

  @computed('args.editableEntity.template.id')
  get template() {
    return this.args.editableEntity.template as TemplateModel;
  }

  @computed('environment.current.identities.@each.templateType')
  get identities() {
    return _sortBy(
      this.environment.current.identities.filter((item) => item.templateType === 'email'),
      ['disabled'],
    );
  }

  @action
  subscribeToRollback() {
    this.eventBus.subscribe(EMAIL_COMPOSER_ROLLBACK, this, this.setTemplate);
  }

  @action
  unsubscribeFromRollback() {
    this.eventBus.unsubscribe(EMAIL_COMPOSER_ROLLBACK, this, this.setTemplate);
  }

  @action
  onTemplateSwitch() {
    this.setTemplate();
  }

  @action
  onSelectAsset(assetPath: string) {
    let code = assetPath?.toLowerCase().endsWith('.pdf')
      ? `<a href="${assetPath}">PDF</a>`
      : `<img src="${assetPath}" alt="" />`;

    this.framecast?.call('insertCode', code);
    this.showAssetLibraryModal = false;
  }
}
